import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Divider,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useSelector } from "react-redux";
import { vendorApi } from "../../api/api";
import { useNavigate } from "react-router-dom";
import GradientButton from "../common/GradiantButton";
import Loader from "../common/Loader";

const YourPropertyList = () => {
  const userId = useSelector((state) => state.user.userId);
  console.log(userId);
  const theme = useSelector((state) => state.themeValue.colors);
  const themeMode = useSelector((state) => state.themeValue.theme);
  const [properties, setProperties] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchProperties = async () => {
      try {
        const response = await vendorApi.getAllPropertiesByVendorId(userId);
        if (response.status === 200) setProperties(response.data);
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchProperties();
  }, [userId]);

  const handleCardClick = (property) => {
    if (property.type === "property") navigate("/YourPropertyScreen", { state: { property } });
    else if (property.type === "project") navigate("/YourProjectScreen", { state: { property } });
    else if (property.type === "villa") navigate("/YourVillaScreen", { state: { property } });
    else if (property.type === "apartment") navigate("/YourApartmentScreen", { state: { property } });
  };

  const filteredProperties = properties.filter((property) => {
    const type = (property.type || "").toLowerCase();
    const status = (property.Status || "").toLowerCase();
    const matchesType = typeFilter === "all" || type === typeFilter;
    const matchesStatus = statusFilter === "all" || status === statusFilter;
    return matchesType && matchesStatus;
  });

  if (loading) return <Loader />;

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      {/* ===== Type Filter ===== */}
      <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {["all", "property", "project", "villa", "apartment"].map((type) => (
          <Button
            key={type}
            variant={typeFilter === type ? "contained" : "outlined"}
            onClick={() => setTypeFilter(type)}
            sx={{
              textTransform: "capitalize",
              color: theme.text,
              border: `1px solid ${theme.borderColor}`,
              backgroundColor: theme.background,
              borderRadius: typeFilter === type ? "20px" : "8px",
              borderWidth: "2px",
              fontStyle: "italic",
              fontWeight: "bold",
            }}
          >
            {type}
          </Button>
        ))}
      </Box>

      {/* ===== Status Dropdown ===== */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160, backgroundColor: theme.tab_bar_inactive, color: theme.text }}
          >
            {["all", "pending", "inprogress", "approved", "rejected"].map((status) => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ===== Property Grid or No Properties Found ===== */}
      {filteredProperties.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            gap: 4,
          }}
        >
          {filteredProperties.map((property, id) => (
            <Card
              key={id}
              sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
                cursor: "pointer",
                backgroundColor: theme.background,
                boxShadow:
                  themeMode === "light"
                    ? "0 4px 16px rgba(0, 0, 0, 0.2)"
                    : "0 4px 16px rgba(255, 255, 255, 0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": { transform: "translateY(-5px)", boxShadow: "0 6px 24px rgba(0, 0, 0, 0.12)" },
              }}
              onClick={() => handleCardClick(property)}
            >
              <CardMedia
                component="img"
                height="180"
                image={property.iconUrl || "/placeholder.jpg"}
                alt={property.name}
                sx={{ objectFit: "cover" }}
              />
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: theme.text }} gutterBottom>
                  {property.name}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.text }}>
                  <strong>Type:</strong> {property.type}
                </Typography>
                <Divider sx={{ my: 1, backgroundColor: theme.secondary_text_color }} />
                <Typography variant="body2" sx={{ color: theme.text }}>
                  <strong>City:</strong> {property.city}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.text }}>
                  <strong>Address:</strong> {property.address}
                </Typography>
                {property.Numberofplots !== undefined && (
                  <Typography variant="body2" sx={{ color: theme.text }}>
                    <strong>Plots:</strong> {property.Numberofplots}
                  </Typography>
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: "auto", pt: 2 }}>
                  <Typography
                    sx={{
                      backgroundColor: property.type === "project" ? "#e3f2fd" : "#e8f5e9",
                      color: "#333",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                  >
                    {property.Status}
                  </Typography>
                  <GradientButton
                    text="View Details"
                    onClick={() => handleCardClick(property)}
                    style={{
                      width: "auto",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "14px",
                      transition: "background-color 0.3s ease",
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            mt: 4,
          }}
        >
          <Typography variant="h4" fontWeight={600} gutterBottom color={theme.primary_text_color}>
            No Properties Found
          </Typography>
          <Typography variant="body1" color={theme.secondary_text_color} mb={4}>
            No properties match your filter.
          </Typography>
          <GradientButton text="Upload Your Property" onClick={() => navigate("/AddProperty")} width={{ xs: "70%", sm: "40%", md: "40%", lg: "20%" }} />
        </Box>
      )}
    </Box>
  );
};

export default YourPropertyList;